import {
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack,
  TextField
} from "@fluentui/react";

import {
  CNPJ_LENGTH,
  CPF_AND_LINE_NUMBER_LENGTH,
  FIXED_PHONE_NUMBER_LENGTH,
  FORMATTED_CNPJ_LENGTH
} from "../constants";

import { Card } from "@/components/Shared/Card";
import { ClearIcon } from "@/components/Shared/Layouts/components/ClearIcon";
import { formatDocumentOrPhone } from "@/utils/FormatDocumentOrPhone";
import { normalizeDocumentNumber } from "@/utils/NormalizeDocumentNumber";

interface SearchInputProps {
  isFetching: boolean;
  searchValue: string;
  setSearchValue: (value: string) => void;
  handleSearch: () => void;
  handleClearSearch: () => void;
  onDismissError: () => void;
  error?: string;
}

export const SearchInput = ({
  isFetching,
  searchValue,
  setSearchValue,
  handleSearch,
  handleClearSearch,
  onDismissError,
  error
}: SearchInputProps) => {
  const isSearchValueInputValid = (searchValue: string) => {
    const sanitizedSearchValue = normalizeDocumentNumber(searchValue);
    const validLengths = [
      CNPJ_LENGTH,
      CPF_AND_LINE_NUMBER_LENGTH,
      FIXED_PHONE_NUMBER_LENGTH
    ];

    return validLengths.includes(sanitizedSearchValue?.length);
  };

  return (
    <Card as="section" padding="1rem">
      <Stack style={{ alignItems: "center" }} tokens={{ childrenGap: 20 }}>
        <Stack
          as="form"
          horizontal
          styles={{
            root: {
              padding: "0 1rem",
              alignItems: "flex-end",
              selectors: {
                "@media(max-width: 768px)": {
                  flexDirection: "column",
                  alignItems: "stretch"
                }
              }
            }
          }}
          onSubmit={event => {
            event.preventDefault();
            void handleSearch();
          }}
        >
          <TextField
            value={searchValue}
            label="Documento do Cooperado ou linha de telefone:"
            placeholder="Digite aqui o documento do cooperado ou a linha de telefone"
            maxLength={FORMATTED_CNPJ_LENGTH}
            onChange={(_, newValue) => {
              const value = newValue ?? "";
              const formatted = formatDocumentOrPhone(value);
              setSearchValue(formatted);
            }}
            styles={{
              root: {
                width: 430,
                maxWidth: "100%",
                selectors: { "@media(max-width: 768px)": { width: "100%" } }
              },
              fieldGroup: {
                selectors: {
                  ".ms-TextField-suffix": { backgroundColor: "transparent" }
                }
              }
            }}
            onRenderSuffix={() => {
              let suffix: JSX.Element = null;

              if (searchValue) {
                suffix = ClearIcon({
                  handleClearSelection: handleClearSearch,
                  ariaLabel: "Limpar campo",
                  title: "Limpar campo"
                });
              }

              return suffix;
            }}
          />

          <PrimaryButton
            text="Consultar Faturas"
            disabled={isFetching || !isSearchValueInputValid(searchValue)}
            type="submit"
            styles={{
              root: {
                marginLeft: 15,
                selectors: {
                  "@media(max-width: 768px)": { marginLeft: 0, marginTop: 10 }
                }
              }
            }}
          />
        </Stack>

        {error && (
          <MessageBar
            messageBarType={MessageBarType.error}
            isMultiline={false}
            onDismiss={onDismissError}
            styles={{
              root: {
                width: "35%",
                selectors: { "@media(max-width: 768px)": { width: "100%" } }
              }
            }}
          >
            {error}
          </MessageBar>
        )}
      </Stack>
    </Card>
  );
};
